import { MicrosoftAuthManager } from "./Managers/Microsoft/MicrosoftAuthManager";
import { GoogleAuthManager } from "./Managers/Google/GoogleAuthManager";

export type AuthState = {
  isAuthenticated: boolean;
  token: string | null;
  type: "microsoft" | "google";
  providerLoginResult: unknown;
  name: string | null;
  email: string | null;
};

type Listener = (state: AuthState) => void;

export default class AuthManager {
  public isAuthenticated: boolean = false;

  private _token: string | null = null;
  public type: "microsoft" | "google" = "microsoft";
  public logoutCallback: () => void = () => {};
  public providerLoginResult: any = null;
  public listeners: Set<Listener> = new Set();
  public name: string | null = null;
  public email: string | null = null;
  public microsoftAuth: MicrosoftAuthManager | null = null;
  public googleAuth: GoogleAuthManager | null = null;
  private isNotifying: boolean = false; // Prevent recursive notifications

  constructor() {
    this.microsoftAuth = new MicrosoftAuthManager(this);
    this.googleAuth = new GoogleAuthManager(this);
  }
  get token() {
    return this._token;
  }
  set token(value: string | null) {
    this._token = value;
  }

  notifyListeners() {
    // Prevent recursive notifications
    if (this.isNotifying) {
      return;
    }

    this.isNotifying = true;

    try {
      const state = {
        isAuthenticated: this.isAuthenticated,
        token: this.token,
        type: this.type,
        providerLoginResult: this.providerLoginResult,
        name: this.name,
        email: this.email,
      };

      localStorage.setItem("auth", JSON.stringify(state));
      this.listeners.forEach((listener) => {
        try {
          listener(state);
        } catch (error) {
          console.error("Error in auth listener:", error);
        }
      });
    } catch (error) {
      console.error("Error in auth manager notifyListeners:", error);
      throw error;
    } finally {
      this.isNotifying = false;
    }
  }

  public subscribe(listener: Listener) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }
  public async getValidToken() {
    if (this.type === "google") {
      return this._token;
    }
    if (
      this._token &&
      (
        this.providerLoginResult as {
          account: { idTokenClaims: { exp: number } };
        }
      )?.account?.idTokenClaims?.exp &&
      (
        this.providerLoginResult as {
          account: { idTokenClaims: { exp: number } };
        }
      )?.account?.idTokenClaims?.exp >
        Date.now() / 1000
    ) {
      return this.token;
    }

    const gotNewToken = await this.tryLoginSilently();

    // Don't call notifyListeners here since the auth managers already call it
    // if (gotNewToken) {
    //   this.notifyListeners();
    // }
    return gotNewToken ? this.token : null;
  }
  public async tryLoginSilently() {
    if (this.type === "microsoft") {
      return await this.microsoftAuth?.trySignInSilently();
    } else if (this.type === "google") {
      return await this.googleAuth?.trySignInSilently();
    }
    return false;
  }

  public async signIn(provider: "microsoft" | "google") {
    await this.signOut();
    if (provider === "microsoft") {
      return await this.microsoftAuth?.signIn();
    } else if (provider === "google") {
      return await this.googleAuth?.signIn();
    }
    return false;
  }

  public async signOut() {
    if (this.type === "microsoft") {
      return await this.microsoftAuth?.signOut().then((e) => {
        this.isAuthenticated = false;
        this.token = null;
        this.providerLoginResult = null;
        this.name = null;
        this.email = null;
        this.notifyListeners();
        return e;
      });
    } else if (this.type === "google") {
      return await this.googleAuth?.signOut().then((e) => {
        this.isAuthenticated = false;
        this.token = null;
        this.providerLoginResult = null;
        this.name = null;
        this.email = null;
        this.notifyListeners();
        return e;
      });
    }
    this.logoutCallback();
    return false;
  }

  public setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  public toJSON() {
    return {
      isAuthenticated: this.isAuthenticated,
      token: this.token,
      type: this.type,
      providerLoginResult: this.providerLoginResult,
      name: this.name,
      email: this.email,
    };
  }

  public static fromJSON(
    json: string | ReturnType<AuthManager["toJSON"]>
  ): AuthManager {
    const data = typeof json === "string" ? JSON.parse(json) : json;
    const instance = new AuthManager();

    // Restore the state
    instance.isAuthenticated = data.isAuthenticated;
    instance._token = data.token;
    instance.type = data.type;
    instance.providerLoginResult = data.providerLoginResult;
    instance.name = data.name || null;
    instance.email = data.email || null;

    // Don't notify listeners immediately - let the component subscribe first
    // instance.notifyListeners();

    // If authenticated, try to refresh the session asynchronously
    if (instance.isAuthenticated) {
      if (instance.type === "microsoft") {
        instance.microsoftAuth?.trySignInSilently().then((success) => {
          if (!success) {
            // If silent login fails, update state to reflect that
            instance.isAuthenticated = false;
            instance._token = null;
            instance.providerLoginResult = null;
            instance.name = null;
            instance.email = null;
            instance.notifyListeners();
          }
        });
      } else if (instance.type === "google") {
        instance.googleAuth?.trySignInSilently().then((success) => {
          if (!success) {
            // If silent login fails, update state to reflect that
            instance.isAuthenticated = false;
            instance._token = null;
            instance.providerLoginResult = null;
            instance.name = null;
            instance.email = null;
            instance.notifyListeners();
          }
        });
      }
    }
    return instance;
  }

  public static fromStorageOrDefault(): AuthManager {
    const stored = localStorage.getItem("auth");
    if (!stored) return new AuthManager();
    return AuthManager.fromJSON(stored);
  }
}
